import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import './Inventory.css';
import './Battle.css';
import { authButtonStyle, authButtonSmallStyle, authCardStyle } from '../constants/authStyles';
import { tl } from '../utils/inlineI18n';
import InventoryBagGrid from './inventory/InventoryBagGrid';
import isGridItemEquipped from './inventory/isGridItemEquipped';
import { ItemImage, GRID_SIZE, isSlotLocked } from './inventory/inventoryBagGridUtils';
import InventoryItemPopup from './inventory/InventoryItemPopup';
import useInventoryItemPopupActions from './inventory/useInventoryItemPopupActions';
import { usePopupMessage } from '../hooks/usePopupMessage';
import { useCharacter } from '../context/CharacterContext';
import { getActiveLocale } from '../utils/localeFormat';

const INVENTORY_TYPE_CONFIG = [
  { inventoryType: 'weapons', slotType: 'weapon', labelKey: 'slotWeapon', baseKey: 'weaponId' },
  { inventoryType: 'armors', slotType: 'armor', labelKey: 'slotArmor', baseKey: 'armorId' },
  { inventoryType: 'helmets', slotType: 'helmet', labelKey: 'slotHelmet', baseKey: 'helmetId' },
  { inventoryType: 'boots', slotType: 'boot', labelKey: 'slotBoot', baseKey: 'bootId' },
  { inventoryType: 'earrings', slotType: 'earring', labelKey: 'slotEarring', baseKey: 'earringId' },
  { inventoryType: 'necklaces', slotType: 'necklace', labelKey: 'slotNecklace', baseKey: 'necklaceId' },
  { inventoryType: 'shields', slotType: 'shield', labelKey: 'slotShield', baseKey: 'shieldId' },
  { inventoryType: 'pots', slotType: 'pot', labelKey: 'slotPot', baseKey: 'potId' },
  { inventoryType: 'others', slotType: 'other', labelKey: 'slotOther', baseKey: 'otherId' },
];
const STACKABLE = new Set(['pots', 'others']);
const DEFAULT_UNLOCKED = 60;

const bagBgVars = (() => {
  const p = process.env.PUBLIC_URL ?? '';
  return {
    '--inv-slot-bg': `url(${p}/image/menu/kutuuzun.png)`,
    '--inv-adam-bg': `url(${p}/image/menu/adam.png)`,
    '--inv-kutukare-bg': `url(${p}/image/menu/kutukare.png)`,
  };
})();

// Envanterin aynısını (sayfalar + yerleşim) kurar; giyili eşyalar gösterilmez.
const buildBag = (data, t) => {
  const catalogs = data.catalogs || {};
  const items = INVENTORY_TYPE_CONFIG.flatMap((cfg) => (data.inventory?.[cfg.inventoryType] || []).map((it) => {
    const baseItem = (catalogs[cfg.inventoryType] || []).find((b) => b.id === it[cfg.baseKey]);
    if (!baseItem) return null;
    return {
      ...it,
      uniqueKey: `${cfg.inventoryType}-${it.id}`,
      inventoryType: cfg.inventoryType,
      slotType: cfg.slotType,
      categoryLabel: t(cfg.labelKey),
      baseItem,
      count: it.count || 1,
    };
  }).filter(Boolean)).filter((it) => !isGridItemEquipped(it, data.equipment || {}));
  const unlocked = Math.min(GRID_SIZE, Math.max(DEFAULT_UNLOCKED, Number(data.layout?.unlockedSlots) || DEFAULT_UNLOCKED));
  const grid = new Array(GRID_SIZE).fill(null);
  const used = new Set();
  (data.layout?.slots || []).forEach((slot) => {
    const item = items.find((e) => e.uniqueKey === slot.itemKey);
    if (item && slot.slotIndex >= 0 && slot.slotIndex < GRID_SIZE && !isSlotLocked(slot.slotIndex, unlocked)) {
      grid[slot.slotIndex] = item;
      used.add(item.uniqueKey);
    }
  });
  let cursor = 0;
  items.filter((it) => !used.has(it.uniqueKey)).forEach((it) => {
    while (cursor < unlocked && grid[cursor]) cursor += 1;
    if (cursor < unlocked) {
      grid[cursor] = it;
      cursor += 1;
    }
  });
  return { grid, unlocked };
};

// Arkadaşa yang veya eşya gönderme penceresi; gönderilen arkadaşın deposuna düşer.
export default function FriendTransferModal({ target, onClose, flash }) {
  const [tab, setTab] = useState('gold');
  const [gold, setGold] = useState(0);
  const [amount, setAmount] = useState('');
  const [bag, setBag] = useState(null);
  const [page, setPage] = useState(1);
  const [selected, setSelected] = useState(null);
  const [count, setCount] = useState(1);
  const [boxOver, setBoxOver] = useState(false);
  const [ghost, setGhost] = useState(null);
  const [busy, setBusy] = useState(false);
  const [confirm, setConfirm] = useState(null); // { body, label }
  const { t } = useTranslation('inventory');
  const boxRef = useRef(null);
  const bagRef = useRef(null);
  const { setError, setStatus, clearError } = usePopupMessage();
  const { character, setDepotItemCount, refreshCharacter } = useCharacter();
  const username = character?.username || localStorage.getItem('username') || '';
  // Eşyaya basınca envanterdeki ile aynı özellik penceresi açılır.
  const { handleGridItemClick, buildPopupProps } = useInventoryItemPopupActions({
    username,
    onAfterAction: () => setBag(null),
    setError,
    setStatus,
    clearError,
    setDepotItemCount,
    refreshCharacter,
  });
  const itemPopupProps = buildPopupProps();
  const touchRef = useRef(null);

  useEffect(() => {
    axios.get('/api/friends/transfer/items')
      .then((res) => { if (res.data?.success) setGold(res.data.gold || 0); })
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (tab !== 'item' || bag) return;
    axios.get('/api/market/init?username=me')
      .then((res) => setBag(res.data?.success === false ? { grid: [], unlocked: DEFAULT_UNLOCKED } : buildBag(res.data || {}, t)))
      .catch(() => setBag({ grid: [], unlocked: DEFAULT_UNLOCKED }));
  }, [tab, bag, t]);

  const pick = (item) => {
    if (!item) return;
    setSelected(item);
    setCount(item.count || 1);
  };

  const overBox = (x, y) => {
    const r = boxRef.current?.getBoundingClientRect();
    return !!r && x >= r.left && x <= r.right && y >= r.top && y <= r.bottom;
  };

  // Dokunmatik sürükleme: parmak kutunun üstünde bırakılırsa eşya seçilir.
  useEffect(() => {
    const move = (e) => {
      const t = touchRef.current;
      const touch = e.touches?.[0];
      if (!t || !touch) return;
      e.preventDefault();
      setGhost((g) => (g ? { ...g, x: touch.clientX, y: touch.clientY } : g));
      setBoxOver(overBox(touch.clientX, touch.clientY));
    };
    const end = (e) => {
      const t = touchRef.current;
      if (!t) return;
      const touch = e.changedTouches?.[0];
      if (touch && overBox(touch.clientX, touch.clientY)) pick(t.item);
      touchRef.current = null;
      setGhost(null);
      setBoxOver(false);
    };
    window.addEventListener('touchmove', move, { passive: false });
    window.addEventListener('touchend', end);
    window.addEventListener('touchcancel', end);
    return () => {
      window.removeEventListener('touchmove', move);
      window.removeEventListener('touchend', end);
      window.removeEventListener('touchcancel', end);
    };
  }, []);

  const send = async () => {
    if (busy) return;
    let body;
    let label;
    if (tab === 'gold') {
      const n = Math.floor(Number(amount));
      if (!n || n <= 0) {
        flash(tl('Geçerli bir yang miktarı gir', 'Enter a valid amount'), true);
        return;
      }
      body = { target, kind: 'gold', amount: n };
      label = `${n.toLocaleString(getActiveLocale())} yang`;
    } else {
      if (!selected) {
        flash(tl('Göndermek için bir eşyayı kutuya sürükle', 'Drag an item into the box'), true);
        return;
      }
      const stackable = STACKABLE.has(selected.inventoryType);
      const c = stackable ? Math.max(1, Math.min(Math.floor(Number(count)) || 1, selected.count || 1)) : 1;
      body = { target, kind: 'item', inventoryType: selected.inventoryType, userItemId: selected.id, count: c };
      label = `${selected.baseItem?.name || ''}${c > 1 ? ` x${c}` : ''}`;
    }
    setConfirm({ body, label });
  };

  const doSend = async () => {
    if (!confirm || busy) return;
    const { body } = confirm;
    setConfirm(null);
    setBusy(true);
    try {
      const res = await axios.post('/api/friends/transfer', body);
      if (res.data?.success) {
        flash(res.data.message || tl('Gönderildi', 'Sent'));
        onClose();
      } else {
        flash(res.data?.message || tl('Gönderilemedi', 'Failed'), true);
      }
    } catch (e) {
      flash(e.response?.data?.message || tl('Gönderilemedi', 'Failed'), true);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="profile-popup-overlay" role="presentation" onClick={onClose}>
      <div className="profile-popup friend-transfer" role="dialog" aria-modal="true" onClick={(e) => e.stopPropagation()}>
        <div className="profile-popup-head">
          <strong>🎁 {tl('Transfer', 'Transfer')}: {target}</strong>
          <button type="button" className="profile-popup-close" aria-label="close" onClick={onClose}>×</button>
        </div>
        <p className="profile-muted">{tl('Gönderdiğin şey arkadaşının deposuna düşer.', 'It goes to your friend\'s depot.')}</p>
        <div className="friend-transfer-tabs">
          <button type="button" className={`auth-button auth-button--compact${tab === 'gold' ? ' battle-subtab--active' : ''}`} style={authButtonSmallStyle} onClick={() => setTab('gold')}>
            💰 {tl('Yang', 'Yang')}
          </button>
          <button type="button" className={`auth-button auth-button--compact${tab === 'item' ? ' battle-subtab--active' : ''}`} style={authButtonSmallStyle} onClick={() => setTab('item')}>
            📦 {tl('Eşya', 'Item')}
          </button>
        </div>
        <button type="button" className="auth-button" style={authButtonStyle} disabled={busy} onClick={send}>
          {busy ? '…' : tl('Gönder', 'Send')}
        </button>
        {tab === 'gold' ? (
          <div className="friend-transfer-body">
            <p className="profile-muted">{tl('Yangın', 'Your yang')}: {Number(gold || 0).toLocaleString(getActiveLocale())}</p>
            <input
              type="number"
              min="1"
              inputMode="numeric"
              className="achievements-search-input"
              placeholder={tl('Miktar', 'Amount')}
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
            />
          </div>
        ) : (
          <div className="friend-transfer-body" style={bagBgVars}>
            <div
              ref={boxRef}
              className={`friend-transfer-box${boxOver ? ' friend-transfer-box--over' : ''}${selected ? ' friend-transfer-box--full' : ''}`}
              onDragOver={(e) => { e.preventDefault(); setBoxOver(true); }}
              onDragLeave={() => setBoxOver(false)}
              onDrop={(e) => {
                e.preventDefault();
                setBoxOver(false);
                const key = e.dataTransfer.getData('text/plain');
                pick((bag?.grid || []).find((it) => it && it.uniqueKey === key));
              }}
            >
              {selected ? (
                <>
                  <span className="friend-transfer-box-img">
                    <ItemImage imageUrl={selected.baseItem?.imageUrl} alt={selected.baseItem?.name} />
                  </span>
                  <span className="friend-transfer-box-text">
                    <strong>{selected.baseItem?.name}</strong>
                    {STACKABLE.has(selected.inventoryType) && (selected.count || 1) > 1 ? (
                      <label className="friend-transfer-countrow">
                        {tl('Adet', 'Count')} (max {selected.count})
                        <input type="number" min="1" max={selected.count} value={count} onChange={(e) => setCount(e.target.value)} />
                      </label>
                    ) : null}
                  </span>
                  <button type="button" className="profile-popup-close" aria-label="clear" onClick={() => setSelected(null)}>×</button>
                </>
              ) : (
                <span className="profile-muted">{tl('Göndereceğin eşyayı buraya sürükle', 'Drag the item here')}</span>
              )}
            </div>
            {bag === null ? (
              <p className="profile-muted">…</p>
            ) : (
              <div className="inventory-list friend-transfer-bag">
                <InventoryBagGrid
                  gridSlots={bag.grid}
                  currentInventoryPage={page}
                  onPageChange={setPage}
                  unlockedSlotsCount={bag.unlocked}
                  draggable={(slotItem) => !!slotItem}
                  onDragStart={(event, index, item) => {
                    if (!item) return;
                    event.dataTransfer.setData('text/plain', item.uniqueKey);
                    event.dataTransfer.effectAllowed = 'move';
                  }}
                  onDragOver={(event) => event.preventDefault()}
                  onDrop={(event) => event.preventDefault()}
                  onTouchStart={(event, index, item) => {
                    const touch = event.touches?.[0];
                    if (!item || !touch) return;
                    touchRef.current = { item };
                    setGhost({ imageUrl: item.baseItem?.imageUrl, x: touch.clientX, y: touch.clientY });
                  }}
                  onClick={(event, slotItem, index) => slotItem && handleGridItemClick(slotItem, index, bagRef)}
                  areaRef={bagRef}
                  slotKeyPrefix="transfer-inventory"
                  emptySlotTitle=""
                  lockedSlotTitle=""
                  isTouchDragging={!!ghost}
                />
              </div>
            )}
          </div>
        )}
        {itemPopupProps ? <InventoryItemPopup {...itemPopupProps} /> : null}
        {confirm ? (
          <div className="popup-overlay" style={{ zIndex: 3000 }} onClick={(e) => { e.stopPropagation(); setConfirm(null); }}>
            <div className="popup-content auth-card" style={authCardStyle} role="alertdialog" aria-modal="true" onClick={(e) => e.stopPropagation()}>
              <div className="popup-header">
                <h3>🎁 {tl('Transfer', 'Transfer')}</h3>
              </div>
              <div className="popup-body">
                <p>{tl(`${confirm.label} ${target} adlı arkadaşına gönderilsin mi?`, `Send ${confirm.label} to ${target}?`)}</p>
              </div>
              <div className="popup-footer" style={{ display: 'flex', gap: '8px', justifyContent: 'center' }}>
                <button type="button" className="auth-button popup-button" style={authButtonStyle} onClick={doSend}>
                  {tl('Evet', 'Yes')}
                </button>
                <button type="button" className="auth-button popup-button" style={authButtonSmallStyle} onClick={() => setConfirm(null)}>
                  {tl('Hayır', 'No')}
                </button>
              </div>
            </div>
          </div>
        ) : null}
        {ghost ? (
          <div className="touch-drag-ghost" style={{ left: `${ghost.x}px`, top: `${ghost.y}px` }}>
            <ItemImage imageUrl={ghost.imageUrl} alt="" />
          </div>
        ) : null}
      </div>
    </div>
  );
}
